// Turns a human "watch this" link into a URL that is safe to put in an
// iframe `src`.
//
// This is deliberately an allowlist, not a sanitiser. The only thing that ever
// reaches an iframe is a string this module BUILDS from an id it extracted and
// re-validated against a strict character class -- the caller's URL is never
// interpolated, so a crafted link cannot smuggle `javascript:`, a `srcdoc`
// payload, an extra query parameter, or a different host into the embed. Any
// input that is not recognisably a YouTube or Vimeo video returns null and the
// UI falls back to a plain external link.
//
// YouTube uses the -nocookie host: it defers YouTube's tracking cookies until
// the learner actually presses play, which keeps an onboarding page from
// silently profiling an intern who merely opened it.
//
// The matching CSP `frame-src` allowlist lives in src/worker.ts and must name
// exactly these two hosts -- widen one without the other and embeds break with
// nothing in the UI to explain why.

export type VideoEmbed = {
  embedUrl: string;
  provider: "youtube" | "vimeo";
  /** The canonical page to open in a new tab when someone prefers the source. */
  watchUrl: string;
};

// YouTube ids are 11 chars of [A-Za-z0-9_-]; Vimeo ids are digits. Anchored, so
// a trailing "/../" or "?x=y" in a captured group can never survive.
const YOUTUBE_ID = /^[A-Za-z0-9_-]{11}$/;
const VIMEO_ID = /^[0-9]{6,12}$/;

const YOUTUBE_HOSTS = new Set([
  "youtube.com", "www.youtube.com", "m.youtube.com",
  "youtube-nocookie.com", "www.youtube-nocookie.com",
]);

const youtubeIdFrom = (url: URL): string | null => {
  if (url.hostname === "youtu.be") return url.pathname.slice(1).split("/")[0] || null;
  if (!YOUTUBE_HOSTS.has(url.hostname)) return null;
  if (url.pathname === "/watch") return url.searchParams.get("v");
  // /embed/ID and /shorts/ID and /live/ID all carry the id as the 2nd segment.
  const [, first, second] = url.pathname.split("/");
  if (["embed", "shorts", "live", "v"].includes(first)) return second || null;
  return null;
};

const vimeoIdFrom = (url: URL): string | null => {
  if (url.hostname !== "vimeo.com" && url.hostname !== "www.vimeo.com" && url.hostname !== "player.vimeo.com") return null;
  // vimeo.com/224453335 and player.vimeo.com/video/224453335
  const segments = url.pathname.split("/").filter(Boolean);
  if (!segments.length) return null;
  const candidate = segments[0] === "video" ? segments[1] : segments[0];
  return candidate ?? null;
};

/**
 * Returns a safe embed descriptor, or null when the input is not a recognised
 * YouTube/Vimeo video URL. Never throws.
 */
export const toVideoEmbed = (rawUrl: string | null | undefined): VideoEmbed | null => {
  if (typeof rawUrl !== "string" || !rawUrl.trim()) return null;
  let url: URL;
  try {
    url = new URL(rawUrl.trim());
  } catch {
    return null;
  }
  // Rules out javascript:, data:, file: and plain http downgrades in one go.
  if (url.protocol !== "https:") return null;

  const youtubeId = youtubeIdFrom(url);
  if (youtubeId && YOUTUBE_ID.test(youtubeId)) {
    return {
      embedUrl: `https://www.youtube-nocookie.com/embed/${youtubeId}?rel=0`,
      provider: "youtube",
      watchUrl: `https://www.youtube.com/watch?v=${youtubeId}`,
    };
  }

  const vimeoId = vimeoIdFrom(url);
  if (vimeoId && VIMEO_ID.test(vimeoId)) {
    return {
      embedUrl: `https://player.vimeo.com/video/${vimeoId}`,
      provider: "vimeo",
      watchUrl: `https://vimeo.com/${vimeoId}`,
    };
  }

  return null;
};
